import { DocumentSummary } from "./Document.dto";
import { DocumentAccessRequesterType } from "./DocumentSigner.dto";
import { DocumentSignReferrer, DocumentSignReferrerReq, DocumentSignReferrerSummary } from "./DocumentSignReferrer.dto";
import { DocsPdfMetainfo, DocsPdfPage } from "./pdf/DocsPdf.dto";
import { SignSummary } from "./Sign.dto";
import { LoLang } from "../common/common.types";
import { UserAgentEssential } from "../common/flax.types";
import { Paginatable } from "../common/Paginatable.dto";

export class DocumentSignerCriteria {
  /** 서명자별 서명 영역 허용 개수 */
  static MAX_SIGNATURE_COUNT = 10;
  /** 서명자 최대 개수 */
  static MAX_SIGNER_COUNT = 20;
  /** 서명자별 날짜 영역 허용 개수 */
  static MAX_DATE_COUNT = 10;
  /** 서명자별 이메일 한글포함 여부 정규표현식 */
  static EMAIL_CONTAIN_KOR_CHARS_REGEX = /[가-힣]/;
}

export class DocumentSignerDropDownAttrCriteria {
  static readonly MAX_OPTION_ITEM_COUNT = 30;
}

export class DocumentSignCriteria {
  static SIGN_CANCEL_REASON_MAXLEN = 1024;
  static ESIGN_TITLE_MAXLEN = 510;
  static ESIGN_REQUEST_REASON_MAXLEN = 50;
  static ICF_TITLE_MAXLEN = 50;
  static readonly PHONE_REGEX = /^(01[016789]{1})[0-9]{3,4}[0-9]{4}$/;
}

export class DocumentSignerFollowWritingItemCriteria {
  static readonly FOLLOW_WRITING_ITEM_ANSWER_MAX_LEN = 8;
}

export enum DocumentSignerAttrType {
  Name = "NAME",
  Title = "TITLE",
  Company = "COMPANY",
  Email = "EMAIL",
  Date = "DATE",
  DateTimezoneOffset = "DATE-TIMEZONE-OFFSET",
  DateFormat = "DATE-FORMAT",
  SignatureImage = "SIGNATURE-IMAGE",
  Signature = "SIGNATURE", // v2 건별서명
  SigningReason = "SIGNING-REASON",
  Sign = "SIGN", // v1 일괄서명, Deprecated
  DynamicSigner = "DYNAMIC-SIGNER",
  AutomaticSign = "AUTOMATIC-SIGN",
  RequireEmailVerify = "EMAIL-VERIFY",
  RequireIdentityVerify = "IDENTITY-VERIFY",
  IdentityVerifyType = "IDENTITY-VERIFY-TYPE",
  IdentityVerifyValue = "IDENTITY-VERIFY-VALUE",
  SilentMode = "SILENT-MODE",
  ResponsiveItemIndex = "RESPONSIVE-ITEM-INDEX",
  ResponsiveItemType = "RESPONSIVE-ITEM-TYPE",
  ResponsiveItemMissing = "RESPONSIVE-ITEM-MISSING",
  ResponsiveItemWrong = "RESPONSIVE-ITEM-WRONG",
  ResponsiveItemValue = "RESPONSIVE-ITEM-VALUE",
  ResponsiveItemRequiredCount = "RESPONSIVE-ITEM-REQ-COUNT", // 응답 항목 중 필수 항목 개수
  ResponsiveItemFieldIndex = "RESPONSIVE-ITEM-FIELD-INDEX",
  ResponsiveItemFieldRequired = "RESPONSIVE-ITEM-FIELD-REQ",
  ResponsiveItemFieldLabel = "RESPONSIVE-ITEM-FIELD-LABEL",
  ResponsiveItemFieldMark = "RESPONSIVE-ITEM-FIELD-MARK",
  ResponsiveItemFieldAltText = "RESPONSIVE-ITEM-FIELD-ALT-TEXT",
  StampApiIndex = "STAMP-API-INDEX",
  StampApiUrl = "STAMP-API-URL",
  StampApiTiming = "STAMP-API-TIMING",
  StampApiMark = "STAMP-API-MARK",

  InputItemIndex = "INPUT-ITEM-INDEX", // 커스텀 입력항목 인덱스 (커스텀 입력항목의 부모)
  InputItemValue = "INPUT-ITEM-VALUE", // 커스텀 입력항목 값
  InputItemMark = "INPUT-ITEM-MARK", // 커스텀 입력항목의 마커 위치
  InputItemMissing = "INPUT-ITEM-MISSING", // 커스텀 입력항목의 미입력 message 및 placeholder
  InputItemRegex = "INPUT-ITEM-REGEX", // 커스텀 입력항목의 정규식
  InputItemCss = "INPUT-ITEM-CSS", // 커스텀 입력항목의 CSS

  DropdownItemIndex = "DROPDOWN-ITEM-INDEX", // 드롭다운 항목 인덱스
  DropdownItemValue = "DROPDOWN-ITEM-VALUE", // 드롭다운 항목 값
  DropdownItemMark = "DROPDOWN-ITEM-MARK", // 드롭다운 항목의 마커 위치
  DropdownItemMissing = "DROPDOWN-ITEM-MISSING", // 드롭다운 항목의 미선택 message 및 placeholder
  DropdownItemOptions = "DROPDOWN-ITEM-OPTIONS", // 드롭다운 항목의 옵션

  AddressItemIndex = "ADDRESS-ITEM-INDEX", // 주소 항목 인덱스
  AddressItemValue = "ADDRESS-ITEM-VALUE", // 주소 항목 값
  AddressItemMark = "ADDRESS-ITEM-MARK", // 주소 항목의 마커 위치
  AddressItemMissing = "ADDRESS-ITEM-MISSING", // 주소 항목의 미입력 message 및 placeholder

  DateItemIndex = "DATE-ITEM-INDEX", // 날짜 항목 인덱스
  DateItemValue = "DATE-ITEM-VALUE", // 날짜 항목 값
  DateItemMark = "DATE-ITEM-MARK", // 날짜 항목의 마커 위치
  DateItemMissing = "DATE-ITEM-MISSING", // 날짜 항목의 미입력 message 및 placeholder
  DateItemFormat = "DATE-ITEM-FORMAT", // 날짜 항목의 날짜 형식

  FollowWritingItemIndex = "FOLLOW-WRITING-ITEM-INDEX", // 서명자의 따라쓰는 항목 인덱스
  FollowWritingItemValue = "FOLLOW-WRITING-ITEM-VALUE", // 서명자의 따라쓰는 항목 값
  FollowWritingItemAnswer = "FOLLOW-WRITING-ITEM-ANSWER", // 서명자의 따라쓰는 항목 답변
  FollowWritingItemMark = "FOLLOW-WRITING-ITEM-MARK", // 서명자의 따라쓰는 항목의 마커 위치
  FollowWritingItemMissing = "FOLLOW-WRITING-ITEM-MISSING", // 서명자의 따라쓰는 항목의 미입력 message 및 placeholder

  // TODO: AgentAvailableValue 사용 여부 논의 필요
  AgentAvailableMark = "AGENT-AVAILABLE-MARK", // 대리인 활성화 유무 및 마커 위치

  AgentResponsiveItemIndex = "AGENT-RESPONSIVE-ITEM-INDEX",
  AgentResponsiveItemType = "AGENT-RESPONSIVE-ITEM-TYPE",
  AgentResponsiveItemMissing = "AGENT-RESPONSIVE-ITEM-MISSING",
  AgentResponsiveItemWrong = "AGENT-RESPONSIVE-ITEM-WRONG",
  AgentResponsiveItemValue = "AGENT-RESPONSIVE-ITEM-VALUE",
  AgentResponsiveItemFieldIndex = "AGENT-RESPONSIVE-ITEM-FIELD-INDEX",
  AgentResponsiveItemFieldRequired = "AGENT-RESPONSIVE-ITEM-FIELD-REQ",
  AgentResponsiveItemFieldLabel = "AGENT-RESPONSIVE-ITEM-FIELD-LABEL",
  AgentResponsiveItemFieldMark = "AGENT-RESPONSIVE-ITEM-FIELD-MARK",
  AgentResponsiveItemFieldAltText = "AGENT-RESPONSIVE-ITEM-FIELD-ALT-TEXT",
  AgentResponsiveItemRequiredCount = "AGENT-RESPONSIVE-ITEM-REQ-COUNT", // 응답 항목 중 필수 항목 개수

  AgentInputItemIndex = "AGENT-INPUT-ITEM-INDEX", // 대리인 커스텀 입력항목 인덱스
  AgentInputItemValue = "AGENT-INPUT-ITEM-VALUE", // 대리인 커스텀 입력항목 값
  AgentInputItemMark = "AGENT-INPUT-ITEM-MARK", // 대리인 커스텀 입력항목의 마커 위치
  AgentInputItemMissing = "AGENT-INPUT-ITEM-MISSING", // 대리인 커스텀 입력항목의 미입력 message 및 placeholder
  AgentInputItemRegex = "AGENT-INPUT-ITEM-REGEX", // 대리인 커스텀 입력항목의 정규식
  AgentInputItemCss = "AGENT-INPUT-ITEM-CSS", // 대리인 커스텀 입력항목의 CSS

  AgentDropdownItemIndex = "AGENT-DROPDOWN-ITEM-INDEX", // 대리인 드롭다운 항목 인덱스
  AgentDropdownItemValue = "AGENT-DROPDOWN-ITEM-VALUE", // 대리인 드롭다운 항목 값
  AgentDropdownItemMark = "AGENT-DROPDOWN-ITEM-MARK", // 대리인 드롭다운 항목의 마커 위치
  AgentDropdownItemMissing = "AGENT-DROPDOWN-ITEM-MISSING", // 대리인 드롭다운 항목의 미선택 message 및 placeholder
  AgentDropdownItemOptions = "AGENT-DROPDOWN-ITEM-OPTIONS", // 대리인 드롭다운 항목의 옵션

  AgentAddressItemIndex = "AGENT-ADDRESS-ITEM-INDEX", // 대리인 주소 항목 인덱스
  AgentAddressItemValue = "AGENT-ADDRESS-ITEM-VALUE", // 대리인 주소 항목 값
  AgentAddressItemMark = "AGENT-ADDRESS-ITEM-MARK", // 대리인 주소 항목의 마커 위치
  AgentAddressItemMissing = "AGENT-ADDRESS-ITEM-MISSING", // 대리인 주소 항목의 미입력 message 및 placeholder

  AgentDateItemIndex = "AGENT-DATE-ITEM-INDEX", // 대리인 날짜 항목 인덱스
  AgentDateItemValue = "AGENT-DATE-ITEM-VALUE", // 대리인 날짜 항목 값
  AgentDateItemMark = "AGENT-DATE-ITEM-MARK", // 대리인 날짜 항목의 마커 위치
  AgentDateItemMissing = "AGENT-DATE-ITEM-MISSING", // 대리인 날짜 항목의 미입력 message 및 placeholder
  AgentDateItemFormat = "AGENT-DATE-ITEM-FORMAT", // 대리인 날짜 항목의 날짜 형식

  AgentSignature = "AGENT-SIGNATURE", // 대리인 서명
  AgentSignatureImage = "AGENT-SIGNATURE-IMAGE", // 대리인 직인

  AgentFollowWritingItemIndex = "AGENT-FOLLOW-WRITING-ITEM-INDEX", // 대리인 따라쓰는 항목 인덱스
  AgentFollowWritingItemValue = "AGENT-FOLLOW-WRITING-ITEM-VALUE", // 대리인 따라쓰는 항목 값
  AgentFollowWritingItemAnswer = "AGENT-FOLLOW-WRITING-ITEM-ANSWER", // 대리인 따라쓰는 항목 답변
  AgentFollowWritingItemMark = "AGENT-FOLLOW-WRITING-ITEM-MARK", // 대리인 따라쓰는 항목의 마커 위치
  AgentFollowWritingItemMissing = "AGENT-FOLLOW-WRITING-ITEM-MISSING", // 대리인 따라쓰는 항목의 미입력 message 및 placeholder

  // 아래 필드는 FE에서만 사용
  SignerIndex = "SIGNER-INDEX",
  AgentIndex = "AGENT-INDEX", // 대리인 항목의 부모
  TemplateIndex = "TEMPLATE-INDEX",
  TemplateMark = "TEMPLATE-MARK",
}

export enum DocumentSignerResponsiveItemTypeValue {
  Multiple = "MULTIPLE", // default
  Single = "SINGLE",
}

export enum AcceptableStampImageMime {
  Png = "PNG",
  Jpg = "JPG",
  Jpeg = "JPEG",
}

export interface DocumentSignerInputItemCss {
  width?: number;
  height?: number;
  fontSize?: number;
  fontFamily?: string;
  padding?: [number, number, number, number];
  lineHeight?: number;
}

export enum DocumentSignerIdentityVerifyType {
  CI = "CI",
}

export enum DocumentSignStatus {
  None = "NONE",
  Pending = "PENDING",
  Creating = "CREATING",
  CreatingFailed = "CREATING-FAILED",
  Wait = "WAIT",
  Delivering = "DELIVERING", // 시작 알림 Fetching
  DeliveringFailed = "DELIVERING-FAILED", // 시작 알림 Fetching Failed
  Delivered = "DELIVERED", // 시작 알림 Done
  SignerDeliveringFailed = "SIGNER-DELIVERING-FAILED", // 서명자 알림 메일 발송 실패
  Signing = "SIGNING", // 서명 중
  Signed = "SIGNED", // 서명 완료 (서명완료 이후 FIANLIZING 단계에서 메일 발송)
  Finalizing = "FINALIZING", // 완료 메일 알림 Fetching
  FinalizingFailed = "FINALIZING-FAILED", // 완료 메일 알림 Fetching Failed
  Finalized = "FINALIZED", // 완료 메일 알림 Done
  Decline = "DECLINE", // 거절 요청
  Declining = "DECLINING", // 거절 메일 알림 Fetching
  DecliningFailed = "DECLINING-FAILED", // 거절 메일 알림 Fetching
  Declined = "DECLINED", // 거절 메일 알림 Done
  Expiring = "EXPIRING",
  ExpiringFailed = "EXPIRING-FAILED",
  Expired = "EXPIRED",
  Cancel = "CANCEL", // 서명취소
  Canceling = "CANCELING", // 서명취소 Fetching
  CancelingFailed = "CANCELING-FAILED", // 서명취소 Fetching Failed
  Canceled = "CANCELED", // 서명취소
}

export enum DocumentSignFilterableStatus {
  SchedulingCompleted = "SCHEDULING_COMPLETED",
  Scheduling = "SCHEDULING",
  SchedulingCanceled = "SCHEDULING_CANCELED",
  Registered = "REGISTERED",
  Creating = "CREATING",
  CreatingFailed = "CREATING_FAILED",
  WaitForSigning = "WAIT_FOR_SIGNING",
  SigningInProgress = "SIGNING_IN_PROGRESS",
  FailedDeliveryDocument = "FAILED_DELIVERY_DOCUMENT",
  SigningCompleted = "SIGNING_COMPLETED",
  SigningDeclined = "SIGNING_DECLINED",
  SigningExpired = "SIGNING_EXPIRED",
  SigningCanceled = "SIGNING_CANCELED",
}

export enum DocumentSignDeliveryMethod {
  Email = "EMAIL",
  Sms = "SMS",
}

export enum DocumentSignType {
  ESign = "E-SIGN",
  EConsent = "E-CONSENT",
}

export enum DocumentSignerType {
  Requestor = "REQUESTOR",
  Signer = "SIGNER",
  CarbonCopy = "CARBON-COPY",
}

export enum DocumentSignerStatus {
  None = "NONE",
  Pending = "PENDING",
  Wait = "WAIT",
  Delivering = "DELIVERING",
  DeliveringFailed = "DELIVERING-FAILED",
  DeliveringSkipped = "DELIVERING-SKIPPED",
  Delivered = "DELIVERED",
  Opened = "OPENED",
  Signed = "SIGNED",
  SignedStamping = "SIGNED-STAMPING",
  Stamping = "STAMPING",
  Stamped = "STAMPED",
  Declined = "DECLINED",
}

export enum DocumentSignerSmsDeliveryStatus {
  None = "NONE",
  Wait = "WAIT",
  Delivering = "DELIVERING",
  DeliveringFailed = "DELIVERING-FAILED",
  Delivered = "DELIVERED",
}

export enum DocumentSignerCallbackStatus {
  None = "NONE",
  Wait = "WAIT",
  Requesting = "REQUESTING",
  Requested = "REQUESTED",
  Failed = "FAILED",
  Skipped = "SKIPPED",
}

export enum DocumentSignerAutomaticSignStatus {
  None = "NONE",
  Wait = "WAIT",
  Signing = "SIGNING",
  Signed = "SIGNED",
  Failed = "FAILED",
  Skipped = "SKIPPED",
}

export enum DocumentSignerAttrTarget {
  Signer = "SIGNER",
}

export enum DocumentSignerAttrStampApiTiming {
  AfterSign = "AFTER-SIGN",
  Signing = "SIGNING",
  BeforeSign = "BEFORE-SIGN",
}

export const DocumentSignerAttrDateValueFormat = "YYYYMMDDHHmmssZ";

export enum DocumentSignerAttrDateFormat {
  YYYYMMDD_Hyphen = "YYYY-MM-DD",
  YYYYMMDD_Slash = "YYYY/MM/DD",
  YYYYMMDD_Dot = "YYYY.MM.DD",
  YYYYMMDD_HMSZ_Hyphen = "YYYY-MM-DD HH:MM:SS Z",
  YYYYMMDD_HMSZ_Slash = "YYYY/MM/DD HH:MM:SS Z",
  YYYYMMDD_HMSZ_Dot = "YYYY.MM.DD HH:MM:SS Z",
}

export const DocumentSignerAttrDateFormatGxpCompatible: Array<DocumentSignerAttrDateFormat> = [
  DocumentSignerAttrDateFormat.YYYYMMDD_HMSZ_Hyphen,
  DocumentSignerAttrDateFormat.YYYYMMDD_HMSZ_Slash,
  DocumentSignerAttrDateFormat.YYYYMMDD_HMSZ_Dot,
];

export enum DocumentSignerHistoryType {
  SignScheduled = "SIGN-SCHEDULED",
  SignStart = "SIGN-START",
  SignEnd = "SIGN-END",
  SignDecline = "SIGN-DECLINE",
  SignCancel = "SIGN-CANCEL",
  SignExpired = "SIGN-EXPIRED",
  Delivered = "DELIVERED",
  SmsDelivered = "SMS-DELIVERED",
  Opened = "OPENED",
  ReferrerOpened = "REFERRER-OPENED",
  Signed = "SIGNED",
  Canceled = "CANCELED",
  Declined = "DECLINED",
}

export interface DocumentSignStampApiData<T = any> {
  documentSignId: bigint;
  documentSignerId: bigint;
  signerIndex: number;
  signdate: string;
  signedAt: Date;
  status: DocumentSignerStatus;
  payload: T;
}

export interface DocumentSignCallbackApiData<T = any> {
  documentSignId: bigint;
  documentSignerId: bigint;
  signerIndex: number;
  signdate: string;
  signedAt: Date;
  status: DocumentSignerStatus;
  payload: T;
}

export interface DocumentSignStampApiResponseData {
  type: "TEXT";
  value: string;
}

export interface DocumentSignStampApiResponse {
  stamp: DocumentSignStampApiResponseData;
}

export interface DocumentSigner {
  id: bigint;
  signerIndex: number;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  documentSignType: DocumentSignType;
  status: DocumentSignerStatus;
  attrs: DocumentSignerAttr[];
  contentUrl: string;
  isMine: boolean;
}

export interface DocumentSignerSummary {
  id: bigint;
  signerIndex: number;
  name: string;
  email: string;
  company?: string;
  title?: string;
  documentSignType: DocumentSignType;
  status: DocumentSignerStatus;
  sign?: SignSummary;
  documentSign?: DocumentSignResourceSummary;
  document?: DocumentSummary;
  signingUrl?: string;
  isDestroyed?: boolean;
  updatedAt?: Date;
  createdAt?: Date;
}

export interface DocumentSignerAttr {
  id?: bigint;
  target: DocumentSignerAttrTarget.Signer;
  attr: DocumentSignerAttrType;
  value?: string;
  groupUuid: string;
  attrUuid: string;
  pageIndex: number | null;
  coordX: number | null; // unit: %
  coordY: number | null; // unit: %
}

export interface DocumentSignSummary {
  id: bigint;
  title: string;
}

export interface DocumentSignResourceSummary {
  id: bigint;
  documentId: bigint;
  projectId: bigint;
  folderId: bigint;
  docsPdfId: bigint;
  title: string;
  documentUuid: string;
  status: DocumentSignStatus;
  signers: DocumentSignerSummary[];
  referrers: DocumentSignReferrerSummary[];
  updatedAt: Date;
  createdAt: Date;
}

export interface DocumentSign {
  id: bigint;
  documentId: bigint;
  projectId: bigint;
  folderId: bigint;
  fileId: bigint;
  econsentConfigureId?: bigint;
  title: string;
  status: DocumentSignStatus;
  type: DocumentSignType;
  signers: DocumentSigner[];
  referrers: DocumentSignReferrer[];
  histories: DocumentSignerHistory[];
  docsPdfId: bigint;
  metainfo: DocsPdfMetainfo;
  contentBucket: string;
  contentKey: string;
  documentUrl: string;
  isGxpCompatible: boolean;
  requestReason?: string;
}

export interface DocumentSignResource extends DocumentSign {
  accessToken: string;
  createdAt: Date;
}

export interface DocumentSignResourceReferrerExt extends DocumentSignResource {
  latestDocumentId: bigint;
}

export interface DocumentSignerHistory {
  id: bigint;
  type: DocumentSignerHistoryType;
  reason: string;
  ipAddr: string;
  userAgent: UserAgentEssential;
  actantId: bigint;
  actantEmail: string;
  actantName: string;
  createdAt: Date;
  remindIndex: number;
  signer?: DocumentSignerSummary;
  referrer?: DocumentSignReferrerSummary;
}

export type DocumentSignerReq = Omit<DocumentSigner, "id" | "status" | "documentSignType" | "isMine" | "contentUrl"> & {
  id?: bigint;
};

export interface CreateDocumentSignRequest {
  fileId: bigint;
  title: string;
  requestReason?: string;
  signers: DocumentSignerReq[];
  referrers?: DocumentSignReferrerReq[];
  isGxpCompatible: boolean;
}

export interface CreateDocumentSignResponse {
  documentSign: DocumentSignResourceSummary;
}

export interface CancelDocumentSignsResponse {
  documentSigns: DocumentSignResourceSummary[];
}

export interface DocumentSignerReqBySignFile {
  signerIndex: number;
  identityVerifyType?: "CI";
  identityVerifyValue?: string;
  base64EncodedSignatureImage?: string;
  signdateTimezoneOffset?: number;
}

export interface CreateDocumentSignBySignFileRequest {
  title: string;
  signers: Array<DocumentSignerReqBySignFile>;
  callbackUrl: string;
  callbackPayload: any;
}

export interface CreateDocumentSignBySignFileResponse {
  documentSign: DocumentSignResourceSummary;
}

export interface CancelDocumentSignRequest {
  documentSignId: bigint;
  reason: string;
}

export interface CancelDocumentSignsRequest {
  documentSignIds: bigint[];
  organizationId: bigint;
  folderId: bigint;
  reason: string;
}

export interface CancelDocumentSignResponse {
  documentSign: DocumentSignResourceSummary;
}

export interface RemindDocumentSignerRequest {
  documentSignerId: bigint;
}

export interface RemindDocumentSignerResponse {
  documentSigner: DocumentSignerSummary;
}

export interface GetDocumentSignRequest {
  documentSignId: bigint;
}

export interface GetDocumentSignResponse {
  documentSign: DocumentSignResource;
}

export interface PaginateDocumentSignsRequest {
  organizationId?: bigint;
  title?: string;
  documentESignStatusMsgs?: DocumentSignFilterableStatus[];
  documentUuid?: string;
  page: number;
  rpp: number;
}

export type PaginateDocumentSignsResponse = Paginatable<DocumentSignResourceSummary>;

export interface GetDocumentSignBySignerTokenRequest {
  token: string;
  accessToken?: string;
  emailVerificationUuid?: string;
  emailVerificationCode?: string;
  personalIdentificationEncrypted?: string;
}

export interface GetDocumentSignBySignerTokenResponse {
  documentSign: DocumentSignResource;
}

export interface CreateEmailVerificationCodeBySignerTokenRequest {
  token: string;
  email: string;
  lang: LoLang;
  documentAccessRequesterType?: DocumentAccessRequesterType;
}

export interface DownloadCertificateBySignerTokenRequest {
  token: string;
  documentAccessRequesterType?: DocumentAccessRequesterType;
  accessToken?: string;
}

export interface DownloadCertificateBySignerTokenResponse {
  url: string;
}

export interface DocumentSignerSignature {
  attrUuid: string;
  signature: string | { key: string; bucket: string };
  verificationUuid: string;
  verificationCode: string;
  width?: number;
  height?: number;
}

export interface SignDocumentWithSignerTokenRequest {
  token: string;
  accessToken: string;
  signature: string;
  signatures: DocumentSignerSignature[];
  attrs: DocumentSignerAttr[];
  dryrun: boolean;
}

export interface SignDocumentWithSignerTokenResponse {
  documentSign: DocumentSignResource;
}

export interface DeclineDocumentWithSignerTokenRequest {
  token: string;
  accessToken: string;
}

export interface DeclineDocumentWithSignerTokenResponse {
  documentSign: DocumentSignResource;
}

export interface CreateDocumentSignsRequest {
  folderId: bigint;
  fileId: bigint;
  signers: DocumentSignerReq[];
  additionalSigners: DocumentSignerReq[];
  deliveryMethods: DocumentSignDeliveryMethod[];
  organizationName: string;
  organizationNameEn: string;
  smsMsg?: string;
  emailImageBucket?: string;
  emailImageKey?: string;
  emailImageKeyEn?: string;
  emailImageName?: string;
  emailImageNameEn?: string;
  emailImageAltText?: string;
  requestReason?: string;
}

export interface CreateDocumentSignsResponse {
  documentSigns: DocumentSignResourceSummary[];
  failedSignerEmails: string[];
}

export interface GetDocumentSignersRequest {
  organizationId: bigint;
  folderId: bigint;
  includeDeletedResource?: boolean;
}

export type GetDocumentSignersResponse = DocumentSignerSummary[];

export interface GetDocumentSignDocsPdfPageRequest {
  documentSignId: bigint;
  docsPdfId: bigint;
  page: number;
}

export interface GetDocumentSignDocsPdfPageResponse {
  docsPdfPage: DocsPdfPage;
}
